import React, { useState } from 'react';
import { toast } from 'sonner';
import { useStore } from '../../contexts/StoreContext';
import { Field } from '../ui/Field';
import { NumberInput } from './NumberInput';
import { OfferEditor } from './OfferEditor';
import { bodyTypes, segments } from '../../data/options';
import { Availability, BodyType, Segment, Vehicle } from '../../types/catalog';
interface VehicleEditorProps {
  vehicle: Vehicle | null;
  onDone: () => void;
}
const lines = (s: string) => s.split('\n').map(x => x.trim()).filter(Boolean);
const slugify = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
export function VehicleEditor({
  vehicle,
  onDone
}: VehicleEditorProps) {
  const {
    brands,
    vehicles,
    updateVehicle,
    createVehicle
  } = useStore();
  const [draft, setDraft] = useState<Vehicle>(vehicle ?? {
    id: '',
    brandId: brands[0]?.id ?? '',
    model: '',
    version: '',
    year: new Date().getFullYear() + 1,
    bodyType: 'SUV',
    segment: 'Compacto',
    images: [],
    price: 0,
    availability: 'Disponible',
    engine: null,
    transmission: null,
    consumption: null,
    safety: [],
    equipment: [],
    warranty: null,
    offer: null,
    featured: false,
    published: false,
    updatedAt: new Date().toISOString()
  });
  const [imagesText, setImagesText] = useState(draft.images.join('\n'));
  const [safetyText, setSafetyText] = useState(draft.safety.join('\n'));
  const [equipText, setEquipText] = useState(draft.equipment.join('\n'));
  const [error, setError] = useState('');
  const set = <K extends keyof Vehicle,>(k: K, v: Vehicle[K]) => setDraft(d => ({
    ...d,
    [k]: v
  }));
  const brand = brands.find(b => b.id === draft.brandId);
  const onSave = (e: React.FormEvent) => {
    e.preventDefault();
    const imgs = lines(imagesText).filter(u => /^https:\/\//.test(u));
    if (!draft.model.trim() || !draft.version.trim()) return setError('Completá modelo y versión.');
    if (draft.price <= 0) return setError('Ingresá un precio de contado válido.');
    if (imgs.length === 0) return setError('Agregá al menos una fotografía (URL https).');
    if (draft.offer && draft.offer.terms.length === 0) return setError('La condición propia necesita al menos un plazo habilitado.');
    const next: Vehicle = {
      ...draft,
      model: draft.model.trim(),
      version: draft.version.trim(),
      images: imgs,
      safety: lines(safetyText),
      equipment: lines(equipText)
    };
    if (vehicle) {
      updateVehicle(vehicle.id, next);
      toast.success('Unidad actualizada. El cambio quedó registrado en el historial.');
    } else {
      let id = slugify(`${brand?.name ?? ''}-${next.model}-${next.version}`);
      if (vehicles.some(v => v.id === id)) id = `${id}-${Date.now().toString(36)}`;
      createVehicle({
        ...next,
        id,
        updatedAt: new Date().toISOString()
      });
      toast.success('Unidad creada.');
    }
    onDone();
  };
  const nullable = (s: string) => s.trim() ? s.trim() : null;
  return <form onSubmit={onSave} className="space-y-6" noValidate data-id="element-239" data-mp="1ns5rlw2l4l9w">
      <section className="space-y-4" data-id="element-240" data-mp="22i3gke8c9f05">
        <h3 className="text-sm font-semibold" data-id="element-241" data-mp="22h1yhpswns8x">Datos de la unidad</h3>
        <div className="grid gap-4 sm:grid-cols-3" data-id="element-242" data-mp="1xj8nmjgnqa2b">
          <Field label="Marca" htmlFor="ve-brand" data-id="element-243" data-mp="1xjs92a5ingl3">
            <select id="ve-brand" className="input" value={draft.brandId} onChange={e => set('brandId', e.target.value)} data-id="element-244" data-mp="19swb0vagvp5e">
              {brands.map(b => <option key={b.id} value={b.id} data-id="element-245" data-mp="14w5uy7ynwtjz">{b.name}</option>)}
            </select>
          </Field>
          <Field label="Modelo" htmlFor="ve-model" data-id="element-246" data-mp="1ivfar6iyhlnf">
            <input id="ve-model" className="input" value={draft.model} onChange={e => set('model', e.target.value)} maxLength={60} data-id="element-247" data-mp="1erzg2fqg5vdd" />
          </Field>
          <Field label="Versión" htmlFor="ve-version" data-id="element-248" data-mp="2ubcc5u7zdy1j">
            <input id="ve-version" className="input" value={draft.version} onChange={e => set('version', e.target.value)} maxLength={80} data-id="element-249" data-mp="2q8fmxknxr3om" />
          </Field>
          <Field label="Año/modelo" htmlFor="ve-year" data-id="element-250" data-mp="3lk74ntqtt8vr">
            <NumberInput id="ve-year" value={draft.year} onChange={v => set('year', v ?? draft.year)} data-id="element-251" data-mp="3pk7gr7qgd7zn" />
          </Field>
          <Field label="Carrocería" htmlFor="ve-body" data-id="element-252" data-mp="3uh7nzlz1ggsc">
            <select id="ve-body" className="input" value={draft.bodyType} onChange={e => set('bodyType', e.target.value as BodyType)} data-id="element-253" data-mp="3chmjtv79p5q">
              {bodyTypes.map(b => <option key={b} data-id="element-254" data-mp="89hsm4s1urwu">{b}</option>)}
            </select>
          </Field>
          <Field label="Segmento" htmlFor="ve-seg" data-id="element-255" data-mp="cb35hq4l3671">
            <select id="ve-seg" className="input" value={draft.segment} onChange={e => set('segment', e.target.value as Segment)} data-id="element-256" data-mp="h3co0l3dd3za">
              {segments.map(s => <option key={s} data-id="element-257" data-mp="m4tle6mna8b4">{s}</option>)}
            </select>
          </Field>
          <Field label="Precio de contado ($)" htmlFor="ve-price" data-id="element-258" data-mp="r3d8wbgvzs5l">
            <NumberInput id="ve-price" value={draft.price} onChange={v => set('price', v ?? 0)} step={1000} data-id="element-259" data-mp="w03neud1mfow" />
          </Field>
          <Field label="Disponibilidad" htmlFor="ve-av" data-id="element-260" data-mp="2gkijtvibuh3j">
            <select id="ve-av" className="input" value={draft.availability} onChange={e => set('availability', e.target.value as Availability)} data-id="element-261" data-mp="2bjbee33npu01">
              {(['Entrega inmediata', 'Disponible', 'A pedido', 'Sin stock'] as Availability[]).map(a => <option key={a} data-id="element-262" data-mp="21n858dae0v3l">{a}</option>)}
            </select>
          </Field>
          <Field label="Garantía" htmlFor="ve-war" data-id="element-263" data-mp="2z842jde1exk3">
            <input id="ve-war" className="input" value={draft.warranty ?? ''} onChange={e => set('warranty', nullable(e.target.value))} placeholder="No disponible" data-id="element-264" data-mp="2uaca8i9hd9fd" />
          </Field>
        </div>
        <Field label="Fotografías (una URL https por línea)" htmlFor="ve-img" data-id="element-265" data-mp="2kkiynh9c8ub4">
          <textarea id="ve-img" className="textarea font-mono text-xs" rows={3} value={imagesText} onChange={e => setImagesText(e.target.value)} data-id="element-266" data-mp="2kigbd4zklfa3" />
        </Field>
      </section>

      <section className="space-y-4" data-id="element-267" data-mp="3d7nc8amhygyi">
        <h3 className="text-sm font-semibold" data-id="element-268" data-mp="1bbo3hk4usguo">Datos técnicos</h3>
        <p className="text-xs text-mist" data-id="element-269" data-mp="1bcg1t2pu4son">Dejá vacío lo que no esté confirmado: el sitio mostrará “No disponible”.</p>
        <div className="grid gap-4 sm:grid-cols-3" data-id="element-270" data-mp="1gck8k10xy91o">
          <Field label="Motor" htmlFor="ve-eng" data-id="element-271" data-mp="11ktkcomwlxdg">
            <input id="ve-eng" className="input" value={draft.engine ?? ''} onChange={e => set('engine', nullable(e.target.value))} data-id="element-272" data-mp="11k0io22sxy26" />
          </Field>
          <Field label="Transmisión" htmlFor="ve-tr" data-id="element-273" data-mp="sk3tlzci2x6l">
            <input id="ve-tr" className="input" value={draft.transmission ?? ''} onChange={e => set('transmission', nullable(e.target.value))} data-id="element-274" data-mp="wms1o4te402i" />
          </Field>
          <Field label="Consumo" htmlFor="ve-cons" data-id="element-275" data-mp="iotnp0ize5nj">
            <input id="ve-cons" className="input" value={draft.consumption ?? ''} onChange={e => set('consumption', nullable(e.target.value))} data-id="element-276" data-mp="nlk45oyp739h" />
          </Field>
        </div>
        <div className="grid gap-4 sm:grid-cols-2" data-id="element-277" data-mp="2i034plzoiwov">
          <Field label="Seguridad (uno por línea)" htmlFor="ve-saf" data-id="element-278" data-mp="2hys4fw5g072i">
            <textarea id="ve-saf" className="textarea" rows={3} value={safetyText} onChange={e => setSafetyText(e.target.value)} data-id="element-279" data-mp="6c82xyut3wno" />
          </Field>
          <Field label="Equipamiento (uno por línea)" htmlFor="ve-eq" data-id="element-280" data-mp="1fhkj4vj4oz3">
            <textarea id="ve-eq" className="textarea" rows={3} value={equipText} onChange={e => setEquipText(e.target.value)} data-id="element-281" data-mp="g3chixgklfhz" />
          </Field>
        </div>
      </section>

      <section className="space-y-4" data-id="element-282" data-mp="p2ppflp1pw0l">
        <h3 className="text-sm font-semibold" data-id="element-283" data-mp="p1o7cx9m499d">Condiciones financieras</h3>
        <div className="grid gap-2 sm:grid-cols-2" data-id="element-284" data-mp="k34hwmvt8fc9">
          <label className={`cursor-pointer rounded-xl border p-3 text-sm ${!draft.offer ? 'border-neon bg-neon/10' : 'border-line text-mist'}`} data-id="element-285" data-mp="k6ihjnd3aag0">
            <input type="radio" name="ve-offer" className="sr-only" checked={!draft.offer} onChange={() => set('offer', null)} data-id="element-286" data-mp="u3djkg8kmsz1" />
            <span className="font-semibold text-white" data-id="element-287" data-mp="30t1tn2pw2n6z">Usar condiciones de {brand?.name ?? 'la marca'}</span>
            <span className="block text-xs" data-id="element-288" data-mp="2vvryr7jqdjbr">Se aplica la configuración general de la marca.</span>
          </label>
          <label className={`cursor-pointer rounded-xl border p-3 text-sm ${draft.offer ? 'border-neon bg-neon/10' : 'border-line text-mist'}`} data-id="element-289" data-mp="2ezmfctj6c3wk">
            <input type="radio" name="ve-offer" className="sr-only" checked={!!draft.offer} onChange={() => set('offer', {
            ...(brand?.defaultOffer ?? brands[0].defaultOffer),
            verified: false
          })} data-id="element-290" data-mp="2jrdzal7trbdq" />
            <span className="font-semibold text-white" data-id="element-291" data-mp="1r2p390lmlw8u">Condición propia / promoción</span>
            <span className="block text-xs" data-id="element-292" data-mp="1vzysaf53s5w5">Tiene prioridad sobre las condiciones de la marca.</span>
          </label>
        </div>
        {draft.offer && <OfferEditor value={draft.offer} onChange={o => set('offer', o)} idPrefix="ve-of" data-id="element-293" data-mp="211xwfr8o36u5" />}
      </section>

      <section className="flex flex-wrap gap-6 border-t border-line pt-4 text-sm" data-id="element-294" data-mp="24z3q1j8ubv79">
        <label className="flex items-center gap-2" data-id="element-295" data-mp="3ch2ziwcpn7ea">
          <input type="checkbox" checked={draft.published} onChange={e => set('published', e.target.checked)} className="h-4 w-4 accent-[#1FA8FF]" data-id="element-296" data-mp="3gfty77wtqx3i" />
          Publicada en el catálogo
        </label>
        <label className="flex items-center gap-2" data-id="element-297" data-mp="10reqbrpehj41">
          <input type="checkbox" checked={draft.featured} onChange={e => set('featured', e.target.checked)} className="h-4 w-4 accent-[#1FA8FF]" data-id="element-298" data-mp="10u0urigzvii1" />
          Destacada en la portada
        </label>
      </section>

      {error && <p className="text-sm text-danger" role="alert" data-id="element-299" data-mp="m2ikm1atgi6l">{error}</p>}
      <div className="sticky bottom-0 -mx-5 -mb-5 flex justify-end gap-2 border-t border-line bg-surface p-4" data-id="element-300" data-mp="m0nwyf85ubyt">
        <button type="button" onClick={onDone} className="btn btn-ghost" data-id="element-301" data-mp="h60fh0bkqhtq">Cancelar</button>
        <button type="submit" className="btn btn-primary" data-id="element-302" data-mp="d46lfavvwc2j">{vehicle ? 'Guardar cambios' : 'Crear unidad'}</button>
      </div>
    </form>;
}